import type { MaintenanceStatus } from '@/hooks/useMaintenanceStatus';
import { useLanguage } from '@/context/LanguageContext';
import { MaintenanceLogin } from './MaintenanceLogin';

interface MaintenanceOverlayProps {
    status: MaintenanceStatus;
    onLoginSuccess: () => void;
}

/**
 * Full-viewport maintenance overlay. Renders null unless maintenance is
 * active AND the current visitor is not allowed (admin or allowlisted).
 * The admin-authored maintenance_message is rendered verbatim — it is
 * content, never run through t().
 */
export function MaintenanceOverlay({ status, onLoginSuccess }: MaintenanceOverlayProps) {
    const { t } = useLanguage();

    if (!status.active || status.allowed) return null;

    const message = status.maintenance_message?.trim()
        ? status.maintenance_message
        : t('maintenance.overlay_default_message');

    return (
        <div
            className="maintenance-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={t('maintenance.overlay_title')}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                background: 'rgba(0,0,0,0.8)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
            }}
        >
            {/* Staff access — top-right corner */}
            <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                <MaintenanceLogin onSuccess={onLoginSuccess} />
            </div>

            {/* Centered content, shifted up */}
            <div
                className="text-center px-6 max-w-xl"
                style={{ paddingBottom: '15vh' }}
            >
                {/* Primary header — site_name, large and white (title fallback when unset) */}
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8">
                    {status.site_name || t('maintenance.overlay_title')}
                </h1>

                {status.tagline && (
                    <p className="text-sm text-white/60 mb-6">{status.tagline}</p>
                )}

                <p className="text-base text-white/90 whitespace-pre-line">{message}</p>
            </div>
        </div>
    );
}
